import React, { useEffect, useState } from 'react';
import Header from '../../components/Header';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, onSnapshot, updateDoc } from 'firebase/firestore';
import { db } from '../../backend/firebase';
import { ClipLoader } from 'react-spinners';
import './UpdateDetails.css';

const UpdateDetails = () => {
  const { datasetid } = useParams();
  const navigate = useNavigate();
  const [datasetDetails, setDatasetDetails] = useState({});
  const [formData, setFormData] = useState({
    name: '',
    interval: '',
    description: '',
    source: '',
    collectionMethod: '',
    timePeriod: '',
    updateFrequency: '',
    isPublic: true
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const dataRef = doc(db, "datasets", datasetid);
    const unsubscribe = onSnapshot(dataRef, (data) => {
      if (data.exists()) {
        const details = data.data();
        setDatasetDetails(details);
        setFormData({
          name: details.name || '',
          interval: details.interval || '',
          description: details.description || '',
          source: details.source || '',
          collectionMethod: details.collectionMethod || '',
          timePeriod: details.timePeriod || '',
          updateFrequency: details.updateFrequency || '',
          isPublic: details.isPublic !== false
        });
      }
      setLoading(false);
    });
    return () => unsubscribe();
  }, [datasetid]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleUpdate = async () => {
    if (!formData.name.trim()) {
      alert("Dataset name cannot be empty.");
      return;
    }
    
    setSaving(true);
    try {
      const dataRef = doc(db, "datasets", datasetid);
      await updateDoc(dataRef, {
        ...formData,
        lastUpdated: new Date().toISOString()
      });
      alert('Dataset details updated successfully!');
      navigate(-1);
    } catch (error) {
      console.error('Update error:', error);
      alert('Failed to update dataset details.');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    navigate(-1);
  };

  if (loading) {
    return (
      <div className="update-details-container">

        <div className="loading-state">
          <ClipLoader color="#ffffff" size={40} />
          <p>Loading dataset details...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="update-details-container">
      <div className="update-details-content">
        <div className="update-header">
          <h2 className="update-title">Edit Dataset Details</h2>
          <div className="dataset-id-display">
            Dataset ID: <span className="dataset-id-value">{datasetid}</span>
          </div>
        </div>

        <form className="update-form" onSubmit={(e) => e.preventDefault()}>
          <div className="form-section">
            <h3 className="section-title">Basic Information</h3>
            
            <div className="form-group">
              <label className="form-label">Dataset Name *</label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="Please enter the dataset name (excluding any reference to the time interval)"
                required
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Interval</label>
              <input
                type="text"
                name="interval"
                value={formData.interval}
                onChange={handleChange}
                placeholder="Enter the time interval for this dataset (e.g., daily, weekly, monthly)"
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Description</label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                placeholder="Describe the dataset..."
                rows={4}
                className="form-textarea"
              />
            </div>
          </div>

          <div className="form-section">
            <h3 className="section-title">Data Source & Collection</h3>
            
            <div className="form-group">
              <label className="form-label">Source</label>
              <input
                type="text"
                name="source"
                value={formData.source}
                onChange={handleChange}
                placeholder="Where does this data come from?"
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Collection Method</label>
              <input
                type="text"
                name="collectionMethod"
                value={formData.collectionMethod}
                onChange={handleChange}
                placeholder="How was this data collected?"
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Time Period</label>
              <input
                type="text"
                name="timePeriod"
                value={formData.timePeriod}
                onChange={handleChange}
                placeholder="What time period does this data cover? e.g (START-END)"
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Update Frequency</label>
              <select
                name="updateFrequency"
                value={formData.updateFrequency}
                onChange={handleChange}
                className="form-select"
              >
                <option value="">Select frequency...</option>
                <option value="Real-time">Real-time</option>
                <option value="Daily">Daily</option>
                <option value="Weekly">Weekly</option>
                <option value="Monthly">Monthly</option>
                <option value="Quarterly">Quarterly</option>
                <option value="Yearly">Yearly</option>
                <option value="Irregular">Irregular</option>
                <option value="One-time">One-time</option>
              </select>
            </div>
          </div>

          <div className="form-section">
            <h3 className="section-title">Access Settings</h3>
            
            <div className="form-group">
              <label className="form-label checkbox-label">
                <input
                  type="checkbox"
                  name="isPublic"
                  checked={formData.isPublic}
                  onChange={handleChange}
                  className="checkbox-input"
                />
                <span className="checkbox-custom"></span>
                Public Dataset
              </label>
              <p className="form-hint">
                {formData.isPublic 
                  ? "This dataset is visible to all users" 
                  : "This dataset is private and only accessible to authorized users"
                }
              </p>
            </div>
          </div>

          <div className="form-actions">
            <button
              type="button"
              onClick={handleCancel}
              className="cancel-button"
              disabled={saving}
            >
              Cancel
            </button>
            <button
              onClick={handleUpdate}
              disabled={saving || !formData.name.trim()}
              className="update-button"
            >
              {saving ? (
                <>
                  <ClipLoader color="#ffffff" size={16} />
                  Updating...
                </>
              ) : (
                'Update Dataset'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UpdateDetails;